import Cookies from "js-cookie";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useMemo, useState } from "react";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from "recharts";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog/dialog";
import { usePerformanceReportsModal } from "./use-performance-reports-modal";
import {
  getAllPerformanceReportsForEmployeeQueryOptions,
  getSingleEmployeeQueryOptions,
} from "@/config/queries/query-options";
import moment from "moment";
import { convertUTCtoIST } from "@/utils";
import {
  fetchAggregatePerformancePointsForEmployee,
  fetchAllPerformanceLogsOfEmployeeForAdmin,
} from "@/config/queries/admin/admin-queries";
import { DataTable } from "../performance-logs/logs-table/data-table";
import { getColumns } from "../performance-logs/logs-table/columns";
import { ColumnDef } from "@tanstack/react-table";
import { PerformanceLog } from "../performance-logs/logs-table/schema";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select/select";
import { format } from "date-fns";
import { Button } from "@/components/ui/button/button";
import { useCreatePerformanceLog } from "./create-performance-log/use-create-performance-log-modal";
import { CreatePerformanceLog } from "./create-performance-log";

import { EditPreviousMonthReportModal } from "./edit-previous-report";
import { useEditPreviousReportModal } from "./edit-previous-report/use-edit-previous-report-modal";

function generateYears(): number[] {
  const currentYear = new Date().getFullYear();
  const endYear = 2024;
  const years: number[] = [];

  for (let year = currentYear; year >= endYear; year--) {
    years.push(year);
  }

  return years;
}

const months = [
  { label: "January", value: 0 },
  { label: "February", value: 1 },
  { label: "March", value: 2 },
  { label: "April", value: 3 },
  { label: "May", value: 4 },
  { label: "June", value: 5 },
  { label: "July", value: 6 },
  { label: "August", value: 7 },
  { label: "September", value: 8 },
  { label: "October", value: 9 },
  { label: "November", value: 10 },
  { label: "December", value: 11 },
];

const monthOrder = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const renderCustomizedLabel = (props: any) => {
  const { x, y, width, value } = props;
  const radius = 16;

  return (
    <g>
      <circle cx={x + width / 2} cy={y - radius} r={radius} fill="#8884d8" />
      <text
        x={x + width / 2}
        y={y - radius}
        fill="#fff"
        textAnchor="middle"
        dominantBaseline="middle"
      >
        {value}
      </text>
    </g>
  );
};

function getMonthDates(month: number, year: number) {
  // Moment.js starts months from 0, so we subtract 1 from the input month
  const startDate = moment([year, month]).startOf("month").toDate();
  const endDate = moment([year, month]).endOf("month").toDate();

  return {
    startDate,
    endDate,
  };
}

export const PerformanceReportsModal = () => {
  const [dateRange, setDateRange] = useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({
    from: undefined,
    to: undefined,
  });

  const [clickedMonth, setClickedMonth] = useState<string | undefined>(
    undefined
  );

  const handleBarClick = (month: string, year: number) => {
    setClickedMonth(month);
    const realMonth = months.find((m) => m.label === month)?.value as number;

    const { startDate, endDate } = getMonthDates(realMonth, year);

    setDateRange({
      from: startDate,
      to: endDate,
    });
  };

  const years = useMemo(() => generateYears(), []);

  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );

  const key = Cookies.get("key") as string;
  const { open, onOpenChange } = usePerformanceReportsModal();

  const { empId } = useParams() as {
    empId: string;
  };

  const { data: SingleEmployeeData } = useQuery(
    getSingleEmployeeQueryOptions(key, empId)
  );

  const { data, isPending } = useQuery(
    getAllPerformanceReportsForEmployeeQueryOptions(key, {
      year: selectedYear,
      employee: empId,
    })
  );


  const reportChartData = useMemo(() => {
    if (isPending) {
      return [];
    }

    if (
      data === undefined ||
      data?.performanceReports === undefined
      // SingleEmployeeData === undefined ||
      // SingleEmployeeData?.employee === undefined
    ) {
      return [];
    }

    let result = data?.performanceReports?.map((item) => {
      return {
        month: item?.month,
        year: item?.year,
        points: item?.remainingPoints,
      };
    });

    // const currentMonth = {
    //   month: format(new Date(), "MMMM"),
    //   year: new Date().getFullYear(),
    //   points: SingleEmployeeData?.employee?.performancePoints as number,
    // };

    result = [
      ...result,
      // currentMonth
    ];

    return result?.sort((a, b) => {
      return monthOrder.indexOf(a?.month) - monthOrder.indexOf(b?.month);
    });
  }, [data, isPending]);

  const previousMonthReports = useMemo(() => {
    if (isPending) {
      return [];
    }

    if (data === undefined || data?.performanceReports === undefined) {
      return [];
    }

    const result = data?.performanceReports?.map((item) => {
      return {
        value: item?._id,
        label: item?.month,
        points: item?.remainingPoints,
        misconducts: item?.misconducts
      };
    });

    return result?.sort((a, b) => {
      return monthOrder.indexOf(a?.label) - monthOrder.indexOf(b?.label);
    });
  }, [data, isPending]);

  const {
    data: EmployeePerformanceLogs,
    isPending: isEmpPerformanceLogsPending,
  } = useQuery({
    queryKey: ["performance-logs", dateRange],
    queryFn: () =>
      fetchAllPerformanceLogsOfEmployeeForAdmin(key)({
        employee: empId,
        startdate: convertUTCtoIST(
          new Date(
            new Date(new Date(dateRange?.from as Date).setHours(0)).setMinutes(
              0
            )
          )
        ),
        enddate: convertUTCtoIST(
          new Date(
            new Date(new Date(dateRange?.to as Date).setHours(23)).setMinutes(
              59
            )
          )
        ),
      }),
    enabled: dateRange?.from !== undefined && dateRange?.to !== undefined,
    refetchOnWindowFocus: false,
    retry: 2,
  });

  // reason: z.string(),
  // dateAdded: z.union([z.string(), z.date()]),
  // misconductOn: z.union([z.string(), z.date()]),
  // misconductFor: z.string(),
  // misconductingInvolvingOthers: z.boolean().default(false),

  const { data: EmployeePerformancePoints } = useQuery({
    queryKey: ["performance-agg-points"],
    queryFn: () =>
      fetchAggregatePerformancePointsForEmployee(key)({
        employee: empId,
      }),
    enabled: empId !== "",
    refetchOnWindowFocus: false,
    retry: 2,
  });

  const logsData = useMemo(() => {
    if (isPending) {
      return [];
    }

    if (
      EmployeePerformanceLogs === undefined ||
      EmployeePerformanceLogs?.log === undefined
    ) {
      return [];
    }

    const result = EmployeePerformanceLogs?.log?.map((t) => {
      return {
        id: t?._id,
        deduction: t?.deduction,
        addition: t?.addition || 0,
        reasonPositive: t?.reasonPositive,
        reasonNegative: t?.reasonNegative,
        createdAt: t?.createdAt,
        date: t?.date,
      };
    });

    return result?.sort(
      (a, b) =>
        new Date(b?.createdAt).getTime() - new Date(a?.createdAt).getTime()
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [EmployeePerformanceLogs, isEmpPerformanceLogsPending]);

  const columns = useMemo<ColumnDef<PerformanceLog, unknown>[]>(
    () => getColumns(),
    []
  );

  const [isLogDialogOpen, setIsLogDialogOpen] = useState(false);

  const { onOpenChange: onCreatePerformanceLog } = useCreatePerformanceLog();

  // Previous Month Report Edit Logic
  const { onOpenChange: onEditPreviousMonthModal } =
    useEditPreviousReportModal();

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl overflow-y-scroll max-h-[720px]">
          <DialogHeader>
            <DialogTitle>Employee Performance Reports</DialogTitle>
          </DialogHeader>

          <div className="flex flex-row items-center gap-4">
            <div>Employee: {SingleEmployeeData?.employee?.name}</div>
            <div className="border-l pl-3">
              Current Month: {format(new Date(), "MMMM")}
            </div>

            <div className="border-l pl-3">
              <Button
                size="sm"
                onClick={() => {
                  onCreatePerformanceLog(true);
                }}
              >
                Add Performance Log for Current Month
              </Button>
            </div>
            <div className="border-l pl-3">
              <Button
                size="sm"
                onClick={() => {
                  onEditPreviousMonthModal(true);
                }}
              >
                Edit Previous Month Report
              </Button>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-3 items-center justify-between">
            <div className="">
              <h5>
                Last 3 months average score:{" "}
                <span className="font-semibold">
                  {EmployeePerformancePoints?.average?.toFixed(2)}
                </span>
              </h5>
            </div>
            <div className="self-end">
              <Select
                value={String(selectedYear)}
                onValueChange={(value) => {
                  setSelectedYear(Number(value));
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a year" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectGroup>
                    {years.map((year) => {
                      return (
                        <SelectItem key={year} value={String(year)}>
                          {year}
                        </SelectItem>
                      );
                    })}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-5 h-[500px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                width={500}
                height={300}
                data={reportChartData}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
                barSize={40}
              >
                <XAxis
                  dataKey="month"
                  scale="point"
                  padding={{ left: 40, right: 40 }}
                />
                <YAxis
                  padding={{
                    top: 40,
                  }}
                />
                <Tooltip />
                <Legend />
                <CartesianGrid strokeDasharray="3 3" />

                <Bar
                  className="cursor-pointer"
                  dataKey="points"
                  fill="#8884d8"
                  background={{ fill: "#eee" }}
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  onClick={(data, _) => {
                    const month = data?.month as string;
                    const year = data?.year as number;

                    setIsLogDialogOpen(true);
                    handleBarClick(month, year);
                  }}
                >
                  <LabelList dataKey="points" content={renderCustomizedLabel} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isLogDialogOpen}
        onOpenChange={(value) => {
          if (!value) {
            setDateRange({
              from: undefined,
              to: undefined,
            });
          }

          setIsLogDialogOpen(value);
        }}
      >
        <DialogContent className="max-w-7xl overflow-y-scroll max-h-[580px]">
          <DialogHeader>
            <DialogTitle>
              Employee Performance Logs of {clickedMonth}
            </DialogTitle>
          </DialogHeader>

          <div className="mt-5">
            <DataTable data={logsData} columns={columns} />
          </div>
        </DialogContent>
      </Dialog>

      <CreatePerformanceLog />
      <EditPreviousMonthReportModal previousReports={previousMonthReports} />
    </>
  );
};
